import { Moment } from 'moment'
import React from 'react'

import { BasicViewProps } from 'constants/models'
import { PaginationDetails } from 'constants/pagination'
import {
  ADMINISTRATION_STATUS_OPTIONS,
  ADMINISTRATION_TYPE_OPTIONS,
  AdministerTypeProps,
  AdministrationStatusProps,
} from 'services/administrations/constants'

import {
  Callout,
  DatePicker,
  EmptyState,
  Form,
  Page,
  Pagination,
  SearchBar,
  Section,
  Select,
  Space,
  Spinner,
  Toolbar,
} from 'components'
import { PaginationProps } from 'components/Pagination'
import Table, { TableColumn } from 'components/Table'

import i18n from 'translations'

import { FilterProps } from './ListContainer'
import { StyledTableWrapper } from './ListStyled'

interface ListViewProps extends BasicViewProps, PaginationProps {
  administrationsListTableData: any
  filters: FilterProps
  onAddClick: () => void
  onAdministerTypeChange: (type: AdministerTypeProps) => void
  onAdministrationDateRangeChange: (dateRange: moment.Moment) => void
  onAdministrationStatusChange: (status: AdministrationStatusProps) => void
  onEmergencyDoseClick: () => void
  onEnquiryDateChange: () => void
  onSearchChange: (value: string) => void
  pagination: PaginationDetails
  search?: string
  tableColumns: TableColumn[]
}

const ListView: React.FC<ListViewProps> = ({
  administrationsListTableData,
  errorMessages,
  filters,
  isLoading,
  onAddClick,
  onAdministerTypeChange,
  onAdministrationDateRangeChange,
  onAdministrationStatusChange,
  onEmergencyDoseClick,
  onSearchChange,
  pagination,
  search,
  tableColumns,
}) => {
  const {
    administerType,
    administrationDateRange,
    administrationStatus,
  } = filters

  const renderPagination = () => !isLoading && (
    <Pagination {...pagination} />
  )

  const renderContent = () => {
    if (isLoading) {
      return <Spinner />
    }

    if (!administrationsListTableData?.length) {
      return (
        <EmptyState icon="administration" text1={i18n.t('module:Safeguarding:Administration:List:emptyStateText1')} />
      )
    }

    return (
      <React.Fragment>
        {renderPagination()}
        <StyledTableWrapper>
          <Table columns={tableColumns} data={administrationsListTableData} visualType="flat" />
        </StyledTableWrapper>
        {renderPagination()}
      </React.Fragment>
    )
  }

  const renderFilters = () => (
    <React.Fragment>
      <SearchBar
        placeholder={i18n.t('module:Safeguarding:Administration:List:searchPlaceholder')}
        value={search}
        variant="standard"
        noBackground
        onChange={onSearchChange}
      />
      <Space margin="0 0 10px" />
      <Toolbar>
        <Toolbar.Group>
          <Toolbar.Item>
            <Form.Row
              label={i18n.t('module:Safeguarding:Administration:Filters:administrationDate:label')}
              verticalLabel
            >
              <DatePicker
                placeholder={i18n.t('module:Safeguarding:Administration:Filters:administrationDate:label')}
                value={administrationDateRange}
                clearable
                range
                onChange={onAdministrationDateRangeChange}
              />
            </Form.Row>
          </Toolbar.Item>
          <Toolbar.Item>
            <Form.Row label={i18n.t('module:Safeguarding:Administration:Filters:administerType:label')} verticalLabel>
              <Select
                options={ADMINISTRATION_TYPE_OPTIONS}
                placeholder={i18n.t('global:All')}
                value={administerType}
                onChange={onAdministerTypeChange}
              />
            </Form.Row>
          </Toolbar.Item>
          <Toolbar.Item>
            <Form.Row label={i18n.t('module:Safeguarding:Administration:Filters:status:label')} verticalLabel>
              <Select
                options={ADMINISTRATION_STATUS_OPTIONS}
                placeholder={i18n.t('global:All')}
                value={administrationStatus}
                onChange={onAdministrationStatusChange}
              />
            </Form.Row>
          </Toolbar.Item>
        </Toolbar.Group>
      </Toolbar>
    </React.Fragment>
  )

  return (
    <Page.Section
      actions={(
        <Section.Actions
          primary={[
            {
              icon: 'emergency-dose',
              label: i18n.t('module:Safeguarding:Administration:Add:emergencyDoseButtonLabel'),
              onClick: onEmergencyDoseClick,
            },
          ]}
          secondary={{
            icon: 'plus',
            label: i18n.t('module:Safeguarding:Administration:Add:scheduledDoseButtonLabel'),
            onClick: onAddClick,
          }}
        />
      )}
      title={i18n.t('module:Safeguarding:Administration:List:title')}
    >
      <Callout content={errorMessages} error />
      {renderFilters()}
      {renderContent()}
    </Page.Section>
  )
}

export default ListView
